import { useMutation } from '@tanstack/react-query'

import { authAPI } from '@/api/new-api'
import { toastFactory } from '@/factories'
import i18n from '@/i18n'
import { getApiErrorMessage } from '@/utils/error'

import { useInvalidateIglooCustomPin } from './get-igloo-custom-pin'

export type RevokeIglooCustomPinResponse = {
  revoked: boolean
  // Note: true when the PIN was already revoked before this request — a success with different
  // copy, not an error. It happens when two staff members revoke the same PIN at once.
  alreadyRevoked: boolean
}

const revokeIglooCustomPin = async (clientId: string): Promise<RevokeIglooCustomPinResponse> => {
  const response = await authAPI.delete<RevokeIglooCustomPinResponse>(
    `/igloowork/${clientId}/custom-pin`,
  )
  return response.data
}

export const useRevokeIglooCustomPin = (clientId: string) => {
  const invalidate = useInvalidateIglooCustomPin(clientId)
  return useMutation({
    mutationFn: () => revokeIglooCustomPin(clientId),
    onError: (error) => {
      toastFactory.error(getApiErrorMessage(error))
    },
    onSuccess: async (response) => {
      await invalidate()
      toastFactory.success(
        i18n.t(
          response.alreadyRevoked
            ? 'clients:keylessAccess.igloo.customPin.toasts.alreadyRevoked'
            : 'clients:keylessAccess.igloo.customPin.toasts.revokeSuccess',
        ),
      )
    },
  })
}
